import { useTranslation } from 'react-i18next';
import { InfluencerFeedPost, InfluencerNewProfileHeaderV2, SocialPostType } from '@anytag/graphql';
import { MediaPreviewWithMetrics } from '@anytag/shared-atoms';
import { THEME } from '@anytag/shared-constants';
import { usePostDetailsParam } from '@anytag/shared-hooks';
import { ImageSlider, InfluencerPostDetailsModal } from '@anytag/shared-molecules';
import { bigIntFormatter } from '@anytag/shared-utils';
import { useInfluencerProfileOpacityContent } from '../../../hooks';
import { InfluencerSelectedAccountInfo } from '../../../types';
import { InfluencerProfileFeedPostsWrapper } from '../../shared';

const FEED_PARAMS_KEY = 'feedPopup';

interface InfluencerTwitterProfileFeedPostsProps {
  data?: readonly InfluencerFeedPost[];
  socialAccount: InfluencerSelectedAccountInfo;
  influencerProfile: InfluencerNewProfileHeaderV2;
}

export const InfluencerTwitterProfileFeedPosts = ({
  data = [],
  socialAccount,
  influencerProfile: { id: influencerId, name }
}: InfluencerTwitterProfileFeedPostsProps) => {
  const { t } = useTranslation();
  const { setPostDetailsParam } = usePostDetailsParam(FEED_PARAMS_KEY);
  const { opacityContent } = useInfluencerProfileOpacityContent({
    influencerId,
    socialAccount,
    skipEstimateContent: true
  });

  return (
    <>
      <InfluencerProfileFeedPostsWrapper
        dataNotAvailable={!data.length}
        title={t('HeaderColumn.Feed Posts')}
        moreDetailProps={{
          to: '/influencer/$id/$socialMediaAccountId/scraping_feed_posts',
          params: { socialMediaAccountId: socialAccount.id.toString() },
          search: { filter: { name, type: SocialPostType.TWITTER } }
        }}
        {...opacityContent}
      >
        <ImageSlider total={data.length} css={{ paddingLeft: '16px' }}>
          {data.map(({ id, caption, thumbnail, comments, likes, shares }) => (
            <MediaPreviewWithMetrics
              key={id}
              src={thumbnail}
              caption={caption}
              css={THEME.mediaPreview.default}
              onClick={() => setPostDetailsParam(id)}
              statistics={[
                { label: t('General.Like'), value: bigIntFormatter(likes, 0, { k: true, m: true }) },
                { label: t('General.Reply'), value: bigIntFormatter(comments, 0, { k: true, m: true }) },
                { label: t('General.Retweet'), value: bigIntFormatter(shares, 0, { k: true, m: true }) }
              ]}
              showIcon
            />
          ))}
        </ImageSlider>
      </InfluencerProfileFeedPostsWrapper>

      <InfluencerPostDetailsModal paramKey={FEED_PARAMS_KEY} posts={data.map(({ id }) => ({ id, influencerId }))} />
    </>
  );
};
